export const LIVE_CONTACT_SOURCE = "supabase";

function compactText(value) {
  return String(value || "").trim();
}

function compactArray(value) {
  return Array.isArray(value)
    ? value.map((item) => compactText(item)).filter(Boolean)
    : [];
}

function cleanObject(value) {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value) ? value : {};
}

function cleanMessages(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function idFor(value) {
  return compactText(value?.database_id || value?.id);
}

function initialsFromName(name) {
  const parts = compactText(name).split(/\s+/).filter(Boolean).slice(0, 2);
  return (parts.map((part) => part[0]).join("") || "CN").toUpperCase();
}

export function buildListingContactRpcArgs({ listing, currentUser, subject, message, sourcePage = "listing" } = {}) {
  const errors = [];
  const listingId = compactText(listing?.database_id);
  const senderId = idFor(currentUser);
  const providerUserId = compactText(listing?.provider_id);
  const cleanSubject = compactText(subject);
  const cleanMessage = compactText(message);
  const cleanSourcePage = compactText(sourcePage) || "listing";

  if (!listingId || listing?.source !== LIVE_CONTACT_SOURCE) {
    errors.push({ field: "listing", message: "Choose a live approved listing before contacting a provider." });
  }
  if (!senderId || currentUser?.source !== LIVE_CONTACT_SOURCE) {
    errors.push({ field: "sender", message: "Sign in with a live account before contacting a provider." });
  }
  if (senderId && providerUserId && senderId === providerUserId) {
    errors.push({ field: "recipient", message: "You cannot contact your own listing." });
  }
  if (cleanSubject.length < 3 || cleanSubject.length > 160) {
    errors.push({ field: "subject", message: "Add a subject between 3 and 160 characters." });
  }
  if (cleanMessage.length < 1 || cleanMessage.length > 5000) {
    errors.push({ field: "message", message: "Add a message between 1 and 5000 characters." });
  }

  if (errors.length) return { errors };

  return {
    args: {
      p_listing_id: listingId,
      p_subject: cleanSubject,
      p_body: cleanMessage,
      p_source_page: cleanSourcePage,
    },
  };
}

export function buildThreadReplyInsert({ thread, currentUser, message } = {}) {
  const errors = [];
  const threadId = compactText(thread?.database_id || thread?.id);
  const senderId = idFor(currentUser);
  const cleanMessage = compactText(message);

  if (!threadId || thread?.source !== LIVE_CONTACT_SOURCE) {
    errors.push({ field: "thread", message: "Choose a live message thread before replying." });
  }
  if (!senderId || currentUser?.source !== LIVE_CONTACT_SOURCE) {
    errors.push({ field: "sender", message: "Sign in with a live account before replying." });
  }
  if (thread?.status && compactText(thread.status) !== "active") {
    errors.push({ field: "thread", message: "This conversation is no longer active." });
  }
  if (cleanMessage.length < 1 || cleanMessage.length > 5000) {
    errors.push({ field: "message", message: "Add a message between 1 and 5000 characters." });
  }

  if (errors.length) return { errors };

  return {
    row: {
      thread_id: threadId,
      sender_id: senderId,
      body: cleanMessage,
      status: "sent",
    },
  };
}

export function toAppMessageThread({ row, currentUserId } = {}) {
  if (!row) return null;

  const publicFields = cleanObject(row.other_public_profile_fields);
  const messages = cleanMessages(row.messages).map((message) => {
    const createdAt = message.created_at || message.time || "";
    return {
      id: compactText(message.id),
      sender_id: compactText(message.sender_id),
      from: compactText(message.sender_id) === compactText(currentUserId) ? "me" : "them",
      text: compactText(message.body || message.text),
      status: compactText(message.status) || "sent",
      time: createdAt,
      created_at: createdAt,
    };
  });

  const otherName = compactText(row.other_full_name) || "Coptic Neighbor member";
  const lastMessageAt = row.last_message_at || messages[messages.length - 1]?.created_at || row.created_at || "";

  return {
    id: compactText(row.thread_id),
    database_id: compactText(row.thread_id),
    listing: compactText(row.listing_slug || row.listing_id),
    listing_database_id: compactText(row.listing_id),
    listing_title: compactText(row.listing_title),
    with: compactText(row.other_user_id),
    other_user: {
      id: compactText(row.other_user_id),
      full_name: otherName,
      city: compactText(row.other_city),
      state: compactText(row.other_state),
      avatar_seed: compactText(publicFields.avatar_seed) || initialsFromName(otherName),
      languages: compactArray(publicFields.languages).length ? compactArray(publicFields.languages) : ["English"],
      source: LIVE_CONTACT_SOURCE,
    },
    subject: compactText(row.subject),
    status: compactText(row.status) || "active",
    last: compactText(row.last_message_body || messages[messages.length - 1]?.text),
    last_message_at: lastMessageAt,
    time: lastMessageAt,
    unread: false,
    msgs: Number(row.message_count || messages.length || 0),
    messages,
    source: LIVE_CONTACT_SOURCE,
  };
}
